/**
 * Pre-Launch Performance Sprint · P3 — Sentry browser init.
 *
 * Contract:
 *  - Clean no-op when `REACT_APP_SENTRY_DSN` is unset — production builds ship
 *    identically without Sentry until the founder configures the DSN.
 *  - PII scrubber in beforeSend — no Authorization headers, cookies, passwords,
 *    JWTs, or payment fields ever ship to Sentry.
 *  - Replay integration OFF — free tier quota preservation (Sentry Performance only).
 *  - tracesSampleRate 0.1 — env-tuneable via `REACT_APP_SENTRY_TRACES_SAMPLE_RATE`.
 */
import * as Sentry from "@sentry/react";

let INITIALISED = false;

const SENSITIVE_KEY_PARTS = [
  "authorization", "cookie", "set-cookie", "x-api-key",
  "password", "pass", "token", "jwt", "secret", "card", "payment",
  "cvv", "cvc", "stripe",
];

function isSensitiveKey(k) {
  const lk = String(k).toLowerCase();
  return SENSITIVE_KEY_PARTS.some((p) => lk.includes(p));
}

function scrubHeaders(headers) {
  if (!headers || typeof headers !== "object") return headers;
  const out = {};
  for (const k of Object.keys(headers)) {
    out[k] = isSensitiveKey(k) ? "[Filtered]" : headers[k];
  }
  return out;
}

function scrubData(data) {
  if (!data || typeof data !== "object") return data;
  if (Array.isArray(data)) return data.map(scrubData);
  const out = {};
  for (const k of Object.keys(data)) {
    out[k] = isSensitiveKey(k) ? "[Filtered]" : scrubData(data[k]);
  }
  return out;
}

export function initSentry() {
  if (INITIALISED) return;
  const dsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();
  if (!dsn) return; // clean no-op

  try {
    Sentry.init({
      dsn,
      // Founder-approved 2026-07-01: single-deploy model (preview + prod on
      // one pod) means REACT_APP_SENTRY_ENVIRONMENT does not reliably
      // propagate to prod builds. Default to "production" to mirror the
      // backend posture — preview vs prod differentiation is done in the
      // Sentry UI via URL filter.
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
      release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: parseFloat(
        process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.1",
      ),
      tracePropagationTargets: [/^\/api\//, /colourmatch\.au\/api/],
      beforeSend(event) {
        if (event.user) delete event.user.ip_address;
        if (event.request) {
          if (event.request.headers) event.request.headers = scrubHeaders(event.request.headers);
          if (event.request.data && typeof event.request.data === "object") {
            event.request.data = scrubData(event.request.data);
          }
        }
        return event;
      },
    });
    INITIALISED = true;
  } catch (e) {
    // Never let Sentry break the app.
    // eslint-disable-next-line no-console
    console.warn("Sentry init failed (non-fatal):", e);
  }
}

export function setSentryUser(user) {
  if (!INITIALISED) return;
  try {
    if (!user) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({ id: user.id, email: user.email, role: user.role });
    if (user.role) Sentry.setTag("role", user.role);
    if (user.is_owner) Sentry.setTag("is_owner", "true");
  } catch (_) { /* swallow */ }
}

export function clearSentryUser() {
  if (!INITIALISED) return;
  try { Sentry.setUser(null); } catch (_) { /* swallow */ }
}
